import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import type { HwcgPersonSeatStatus } from 'components/IncidentConfig/roster/rosterPeriods';
import RosterOrgChart, { type RosterOrgChartHandle } from './RosterOrgChart';
import RosterTemplateSidebar from './roster/RosterTemplateSidebar';
import RosterTemplateSeatActions from './roster/RosterTemplateSeatActions';
import Ics207SigningPanel from './roster/Ics207SigningPanel';
import {
  persistSidebarCollapsed,
  readSidebarCollapsed,
  RosterChartChromeButtons,
} from './roster/RosterChartChromeButtons';
import {
  buildRosterFilterChips,
  dismissRosterFilterChip,
  EMPTY_ROSTER_FILTERS,
  filterRosterPositions,
  type RosterFilters,
} from './roster/rosterPositionFilter';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';

type OrganizationChartProps = {
  incidentMembers: RosterEntry[];
  draftIncidentMembers?: RosterEntry[] | null;
  hwcgLaterMembers?: RosterEntry[] | null;
  hwcgPeriodLabels?: Record<string, string>;
  hwcgPersonStatus?: Record<string, HwcgPersonSeatStatus> | null;
  simplified?: boolean;
  /**
   * Draw the organisation for reference only, with nothing on it that writes.
   *
   * `simplified` only drops the toolbar around the chart; the seats stay
   * editable. A form box that prints the organisation needs the seats
   * themselves inert too, and sends the reader to the roster page to change
   * them.
   */
  readOnly?: boolean;
  /** Hide this chart's own toolbar so a parent can own the heading row. */
  hideToolbar?: boolean;
  sidebarCollapsed?: boolean;
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  rosterFilters?: RosterFilters;
  onRosterFiltersChange?: (next: RosterFilters) => void;
  /**
   * When set, only these seats open the position modal. Other seats stay
   * visible. Null leaves every seat clickable.
   */
  editablePositionIds?: string[] | null;
  /** Do not draw the left template menu or the control that opens it. */
  hideTemplateSidebar?: boolean;
  /** Height of the chart area. Defaults to a fixed 600px. */
  chartHeightClassName?: string;
};

export type OrganizationChartHandle = {
  previewIcs207: () => Promise<void>;
};

const OrganizationChart = forwardRef<
  OrganizationChartHandle,
  OrganizationChartProps
>(function OrganizationChart(
  {
    incidentMembers,
    draftIncidentMembers = null,
    hwcgLaterMembers = null,
    hwcgPeriodLabels,
    hwcgPersonStatus = null,
    simplified = false,
    readOnly = false,
    hideToolbar = false,
    sidebarCollapsed: sidebarCollapsedProp,
    onSidebarCollapsedChange,
    rosterFilters: rosterFiltersProp,
    onRosterFiltersChange,
    editablePositionIds = null,
    hideTemplateSidebar = false,
    chartHeightClassName = 'tw:h-[600px]',
  },
  ref
) {
  const { data: positions = [] } = useGetPositionsForIncident();
  const [internalRosterFilters, setInternalRosterFilters] =
    useState<RosterFilters>(EMPTY_ROSTER_FILTERS);
  const [showIcs207Panel, setShowIcs207Panel] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null
  );
  const [internalSidebarCollapsed, setInternalSidebarCollapsed] =
    useState(readSidebarCollapsed);
  const chartRef = useRef<RosterOrgChartHandle>(null);
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const draft = useOptionalCreateRosterDraft();
  const isCreateMode = !!draft;
  const structureOnly = !!draft?.structureOnly;
  const rosterFilters = rosterFiltersProp ?? internalRosterFilters;
  const setRosterFilters = onRosterFiltersChange ?? setInternalRosterFilters;
  const sidebarCollapsed = sidebarCollapsedProp ?? internalSidebarCollapsed;
  const setSidebarCollapsed =
    onSidebarCollapsedChange ?? setInternalSidebarCollapsed;
  const { exportAsForm, isExporting } = icsFormPdf ?? {
    exportAsForm: async () => undefined,
    isExporting: false,
  };

  useEffect(() => {
    persistSidebarCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  const effectiveOccupancy = isCreateMode
    ? rosterFilters.occupancy.filter((option) => option !== 'draft')
    : rosterFilters.occupancy;
  const visiblePositionIds = useMemo(() => {
    if (
      !rosterFilters.search.trim() &&
      rosterFilters.positionIds.length === 0 &&
      rosterFilters.categories.length === 0
    ) {
      return null;
    }
    return new Set(
      filterRosterPositions(positions, rosterFilters).map(
        (position) => position.id
      )
    );
  }, [positions, rosterFilters]);
  const rosterFilterChips = useMemo(
    () => buildRosterFilterChips(rosterFilters, positions),
    [positions, rosterFilters]
  );

  const captureChart = useCallback(
    async () => (await chartRef.current?.captureImage()) ?? null,
    []
  );

  const handlePreviewIcs207 = useCallback(async () => {
    let orgChart: File | null = null;
    try {
      orgChart = await captureChart();
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Could not capture the organization chart.');
      return;
    }

    if (!orgChart) {
      toast.error('There is nothing on the org chart to export.');
      return;
    }

    await exportAsForm({
      formname: 'ICS_207_CG',
      data: {},
      mapSketch: orgChart,
    });
    setShowIcs207Panel(true);
  }, [captureChart, exportAsForm]);

  useImperativeHandle(
    ref,
    () => ({
      previewIcs207: handlePreviewIcs207,
    }),
    [handlePreviewIcs207]
  );

  const chrome = (
    <RosterChartChromeButtons
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapsedChange={setSidebarCollapsed}
      rosterFilters={rosterFilters}
      onRosterFiltersChange={setRosterFilters}
      positions={positions}
      hideDraftOccupancy={isCreateMode}
      iconOnlyFilter={structureOnly}
      onPreviewIcs207={
        !isCreateMode && icsFormPdf ? handlePreviewIcs207 : undefined
      }
      isExporting={isExporting}
      hideTemplateSidebar={hideTemplateSidebar}
    />
  );

  return (
    <div className="dark tw:rounded-md">
      {!simplified && !hideToolbar && (
        <div
          className={
            structureOnly
              ? 'tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2'
              : 'd-md-flex align-items-center mb-3'
          }
        >
          {structureOnly ? <h4 className="m-0">Roster Template</h4> : null}
          <div
            className={
              structureOnly
                ? 'tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-2'
                : 'text-md-end d-flex align-items-center justify-content-end ms-auto flex-wrap gap-2'
            }
          >
            {structureOnly ? <RosterTemplateSeatActions /> : null}
            {chrome}
          </div>
        </div>
      )}
      {rosterFilterChips.length > 0 ? (
        <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          {rosterFilterChips.map((chip) => (
            <Badge
              key={chip.id}
              variant="outline"
              className="tw:h-6 tw:gap-1 tw:pr-1"
            >
              <span className="tw:max-w-[12rem] tw:truncate">{chip.label}</span>
              <button
                type="button"
                className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() =>
                  setRosterFilters(
                    dismissRosterFilterChip(rosterFilters, chip.id)
                  )
                }
              >
                <X className="tw:size-3" />
              </button>
            </Badge>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setRosterFilters(EMPTY_ROSTER_FILTERS)}
          >
            Clear all
          </Button>
        </div>
      ) : null}
      <div
        className={`tw:flex ${chartHeightClassName} tw:min-h-0 tw:items-stretch tw:gap-3`}
      >
        {!simplified &&
        !readOnly &&
        !hideTemplateSidebar &&
        !sidebarCollapsed ? (
          <RosterTemplateSidebar
            selectedPositionId={selectedPositionId}
            onCollapse={() => setSidebarCollapsed(true)}
          />
        ) : null}
        <div className="tw:min-h-0 tw:min-w-0 tw:flex-1">
          <RosterOrgChart
            ref={chartRef}
            incidentMembers={incidentMembers}
            draftIncidentMembers={isCreateMode ? null : draftIncidentMembers}
            hwcgLaterMembers={isCreateMode ? null : hwcgLaterMembers}
            hwcgPeriodLabels={hwcgPeriodLabels}
            hwcgPersonStatus={hwcgPersonStatus}
            occupancyFilter={
              isCreateMode
                ? effectiveOccupancy.filter((option) => option !== 'draft')
                : effectiveOccupancy
            }
            visiblePositionIds={visiblePositionIds}
            editablePositionIds={editablePositionIds}
            colorMode="dark"
            onSelectedPositionIdChange={setSelectedPositionId}
            readOnly={readOnly}
          />
        </div>
      </div>
      {/* Only the real roster signs. The read-only reuse inside the ICS-201 is a
          picture of the organisation, not the form that records it. */}
      {!simplified && !readOnly && !isCreateMode && showIcs207Panel && (
        <div className="tw:mt-3">
          <Ics207SigningPanel captureChart={captureChart} />
        </div>
      )}
    </div>
  );
});

export default OrganizationChart;
